import WModal from "../wmodal/WModal";
import Image from "next/legacy/image";
import { Dispatch, SetStateAction, useState } from "react";
import WProfileInfoEditElement from "./WProfileInfoEditElement";
import WButton from "../wbutton/WButton";
import WTypography from "../wtypography/WTypography";
import WProfileEditAboutMeForm from "./WProfileEditAboutME";
import WProfileEditPersonalDataForm from "./WProfileEditPersonalDataForm";
import WProfileEditPictureForm from "./WProfileEditPictureForm";
import { UsersPermissionsUserQuery } from "@/generated";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import routes from "@/utils/routes";
import WUserAddresses from "./WUserAddresses";

const editTypes = {
  EDIT_PICTURE: "EDIT_PICTURE",
  PERSONAL_DATA: "PERSONAL_DATA",
  ABOUT_ME: "ABOUT_ME",
  ADDRESSES: "ADDRESSES",
};

interface IProfileEditProps {
  profile?:
    | (NonNullable<
        NonNullable<UsersPermissionsUserQuery["usersPermissionsUser"]>["data"]
      >["attributes"] & { id?: string })
    | null
    | undefined;
  onClose: Dispatch<boolean>;
}

export const WProfileEditModal = ({ profile, onClose }: IProfileEditProps) => {
  const [viewEdit, setViewEdit]: [string, Dispatch<SetStateAction<string>>] =
    useState("");

  const { t } = useTranslation("profileEdit");

  const router = useRouter();

  const profileImage = profile?.picture?.data?.attributes?.url
    ? profile?.picture?.data?.attributes?.url
    : "/img/profile-image-placeholder.png";

  const [selectedAddressId, setSelectedAddressId] = useState<string>();

  return (
    <>
      <WModal setModal={() => onClose(false)}>
        <div className="flex flex-col w-full">
          <div className="flex gap-5">
            <div className="relative w-40 h-40 overflow-hidden rounded-xl">
              <Image
                src={profileImage}
                alt={t("profile-image")?.toString()}
                layout="fill"
                objectFit="contain"
              />
            </div>
            <div className="flex flex-col justify-between py-3 text-left">
              <div>
                <WTypography text={t("my-profile")?.toString()} />
                <WTypography
                  text={`${profile?.firstName} ${profile?.lastName}`}
                  type="subtitle"
                />
              </div>
              <WButton
                type="secondary-small"
                text={t("edit-picture")}
                onClick={() => setViewEdit(editTypes.EDIT_PICTURE)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 mt-5">
            <WTypography text={t("my-profile")?.toString()} type="subtitle" />
            <WProfileInfoEditElement
              title={t("personal-data")}
              subTitle={t("personal-data-example")}
              onClick={() => setViewEdit(editTypes.PERSONAL_DATA)}
            />
            <WProfileInfoEditElement
              title={t("about-me")}
              subTitle={t("about-me-example")}
              onClick={() => setViewEdit(editTypes.ABOUT_ME)}
            />

            <WProfileInfoEditElement
              title={t("my-addresses")}
              subTitle={t("edit-your-addresses")}
              onClick={() => setViewEdit(editTypes.ADDRESSES)}
            />

            <WProfileInfoEditElement
              title={t("my-subscriptions")}
              subTitle=""
              onClick={() => router?.push(routes.subscriptions.mySubscriptions)}
            />
            <WProfileInfoEditElement
              title={t("legals")}
              subTitle=""
              onClick={() => router?.push(routes?.legals)}
            />
          </div>
        </div>
      </WModal>
      {viewEdit === editTypes.EDIT_PICTURE && (
        <WModal
          setModal={() => setViewEdit("")}
          title={t("edit-picture")?.toString()}
          subtitle={t("select-profile-picture")?.toString()}
        >
          <WProfileEditPictureForm onDone={() => setViewEdit("")} />
        </WModal>
      )}

      {viewEdit === editTypes.PERSONAL_DATA && (
        <WModal
          setModal={() => setViewEdit("")}
          title={t("personal-data")?.toString()}
          subtitle={t("personal-data-subtitle")?.toString()}
        >
          <div className="flex flex-col w-full gap-3">
            <WProfileEditPersonalDataForm
              profile={profile}
              isProfileEdit
              loading={false}
              onCancel={() => setViewEdit("")}
            />
          </div>
        </WModal>
      )}

      {viewEdit === editTypes.ABOUT_ME && (
        <WModal
          setModal={() => setViewEdit("")}
          title={t("about-me")?.toString()}
          subtitle={t("about-me-subtitle")?.toString()}
        >
          <WProfileEditAboutMeForm onCancel={() => setViewEdit("")} />
        </WModal>
      )}

      {viewEdit === editTypes.ADDRESSES && (
        <WModal large setModal={() => setViewEdit("")}>
          <WUserAddresses selectedAddressId={setSelectedAddressId} />
        </WModal>
      )}
    </>
  );
};

export default WProfileEditModal;
